"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_player_1 = __importDefault(require("react-player"));
const ProjectContext_1 = require("../contexts/ProjectContext");
const SubtitleSegmentRow_1 = __importDefault(require("./SubtitleSegmentRow"));
const SubtitleSourceManager_1 = __importDefault(require("./SubtitleSourceManager"));
const SubtitleComparison_1 = __importDefault(require("./SubtitleComparison"));
const SegmentFilters_1 = __importDefault(require("./SegmentFilters"));
const SourceSelector_1 = __importDefault(require("./SourceSelector"));
const api_1 = require("../services/api");
const lucide_react_1 = require("lucide-react");
const useKeyboardShortcuts_1 = require("../hooks/useKeyboardShortcuts");
const lucide_react_2 = require("lucide-react");
const spellCheckManager_1 = require("../services/spellCheckManager");
const spellCheckService_1 = require("../services/spellCheckService");
const EditorPanel = () => {
    const { state, updateSegment, refreshCurrentProject } = (0, ProjectContext_1.useProjectContext)();
    const [activeSegmentId, setActiveSegmentId] = (0, react_1.useState)(null);
    const [currentTime, setCurrentTime] = (0, react_1.useState)(0);
    const [isPlaying, setIsPlaying] = (0, react_1.useState)(false);
    const [userClickedSegment, setUserClickedSegment] = (0, react_1.useState)(false);
    const [isEditingSegment, setIsEditingSegment] = (0, react_1.useState)(false);
    const playerRef = (0, react_1.useRef)(null);
    const segmentListRef = (0, react_1.useRef)(null);
    // Nové stavy pro správu zdrojů
    const [showSourceManager, setShowSourceManager] = (0, react_1.useState)(false);
    const [showComparison, setShowComparison] = (0, react_1.useState)(false);
    const [currentSourceId, setCurrentSourceId] = (0, react_1.useState)(null);
    const [currentSourceType, setCurrentSourceType] = (0, react_1.useState)(null);
    const [availableSources, setAvailableSources] = (0, react_1.useState)([]);
    const [activeSource, setActiveSource] = (0, react_1.useState)(null);
    // Použij klasické tři listboxy jako výchozí (SegmentFilters)
    const [useContextualFilters, setUseContextualFilters] = (0, react_1.useState)(false);
    // Filtry pro segmenty
    const [filters, setFilters] = (0, react_1.useState)({
        spellCheck: 'all',
        aiCorrection: 'all',
        suggestions: 'all',
        searchText: ''
    });
    // Load available sources when project changes
    (0, react_1.useEffect)(() => {
        const loadSources = async () => {
            if (!state.currentProject) {
                setAvailableSources([]);
                setActiveSource(null);
                return;
            }
            try {
                // Mock API call - replace with actual API
                const sources = await api_1.projectsApi.getProjectSources(state.currentProject.project_id);
                setAvailableSources(sources);
                // Set first completed source as active
                const completedSources = sources.filter(s => s.status === 'completed');
                if (completedSources.length > 0 && !activeSource) {
                    setActiveSource(completedSources[0]);
                    setCurrentSourceId(completedSources[0].source_id);
                }
            }
            catch (error) {
                console.error('Failed to load sources:', error);
                setAvailableSources([]);
                setActiveSource(null);
            }
        };
        loadSources();
    }, [state.currentProject]);
    // Handle source change
    const handleSourceChange = (sourceId) => {
        const source = availableSources.find(s => s.source_id === sourceId);
        if (source) {
            setActiveSource(source);
            setCurrentSourceId(sourceId);
            setCurrentSourceType(source.source_type);
            // Reset filters when changing source
            setFilters({
                spellCheck: 'all',
                aiCorrection: 'all',
                suggestions: 'all',
                searchText: ''
            });
        }
    };
    // Remove duplicate keyboard handler - using useKeyboardShortcuts hook instead
    // Get segments for active source
    const activeSourceSegments = (0, react_1.useMemo)(() => {
        const segments = state.currentProject?.segments || [];
        // If there are any segments but no sources loaded yet, show them all (fallback)
        if (segments.length > 0 && availableSources.length === 0) {
            return segments;
        }
        // If segments don't have source_id at all (older projects), show ALL segments
        const anyWithSource = segments.some(s => !!s.source_id);
        if (!anyWithSource)
            return segments;
        // If there are source-linked segments but no active source is selected yet, return empty
        if (!activeSource)
            return [];
        // Otherwise filter by active source
        return segments.filter(segment => segment.source_id === activeSource.source_id);
    }, [state.currentProject?.segments, activeSource, availableSources.length]);
    // Detekce prázdného projektu - dynamická logika pro 0-N zdrojů
    const hasActiveSegments = activeSourceSegments && activeSourceSegments.length > 0;
    const completedSources = availableSources.filter(s => s.status === 'completed');
    const shouldShowEmptyView = !hasActiveSegments && completedSources.length === 0;
    // Filtrované segmenty (pouze z aktivního zdroje)
    const filteredSegments = (0, react_1.useMemo)(() => {
        if (!activeSourceSegments.length)
            return [];
        return activeSourceSegments.filter((segment) => {
            // Vyhledávání v textu
            if (filters.searchText && filters.searchText.trim() !== '') {
                const searchTerm = filters.searchText.toLowerCase().trim();
                const originalText = (segment.original_text || '').toLowerCase();
                const correctedText = (segment.corrected_text || '').toLowerCase();
                if (!originalText.includes(searchTerm) && !correctedText.includes(searchTerm))
                    return false;
            }
            // Spell check filter
            if (filters.spellCheck !== 'all') {
                const hasSpellErrors = !!segment.spellCheck?.hasErrors;
                if (filters.spellCheck === 'with-errors' && !hasSpellErrors)
                    return false;
                if (filters.spellCheck === 'without-errors' && hasSpellErrors)
                    return false;
            }
            // AI correction filter — používej ai_correction_type z DB
            if (filters.aiCorrection !== 'all') {
                const aiType = segment.ai_correction_type || 'unchecked';
                if (filters.aiCorrection === 'unchanged' && aiType !== 'unchanged')
                    return false;
                if (filters.aiCorrection === 'ai-corrected' && aiType !== 'ai_corrected')
                    return false;
                if (filters.aiCorrection === 'needs-review' && aiType !== 'needs_review')
                    return false;
                if (filters.aiCorrection === 'unprocessed' && aiType !== 'unchecked')
                    return false;
            }
            // Manual editing filter (původní klíč suggestions ve filtrech používáme pro manuální editace)
            if (filters.suggestions !== 'all') {
                const editedCount = segment.manual_edit_count || 0;
                const isEdited = editedCount > 0;
                if (filters.suggestions === 'with-suggestions' && !isEdited)
                    return false;
                if (filters.suggestions === 'without-suggestions' && isEdited)
                    return false;
            }
            return true;
        });
    }, [activeSourceSegments, filters]);
    // Počty pro filtry (pouze pro aktivní zdroj)
    const [spellCacheVersion, setSpellCacheVersion] = (0, react_1.useState)(0);
    (0, react_1.useEffect)(() => {
        // Sleduj změny cache ve spellCheckManageru a přepočti počty
        const unsubscribe = spellCheckManager_1.spellCheckManager.addCacheListener(() => setSpellCacheVersion(v => v + 1));
        return unsubscribe;
    }, []);
    const segmentCounts = (0, react_1.useMemo)(() => {
        if (!activeSourceSegments.length) {
            return {
                total: 0,
                spell_check: { checked: 0, unchecked: 0, with_errors: 0, without_errors: 0, last_check: null },
                ai_correction: { processed: 0, unprocessed: 0, unchanged: 0, ai_corrected: 0, needs_review: 0, last_correction: null },
                manual_editing: { edited_segments: 0, total_edits: 0, last_edit: null },
                review: { needs_review: 0, completion_rate: { spell_check: 0, ai_correction: 0 } }
            };
        }
        const segments = activeSourceSegments;
        const spellChecked = segments.filter(s => s.spellCheck || s.spell_check_status === 'checked');
        const withSpellErrors = segments.filter(s => (s.spellCheck?.hasErrors) || (s.spell_errors_count || 0) > 0);
        const spellCheckedCount = segments.filter(s => s.spellCheck || s.spell_check_status === 'checked' || (s.spell_errors_count || 0) >= 0 && s.spell_check_status).length;
        const aiProcessed = segments.filter(s => s.ai_correction_type && s.ai_correction_type !== 'unchecked');
        const manuallyEdited = segments.filter(s => s.manual_edit_count && s.manual_edit_count > 0);
        return {
            total: segments.length,
            spell_check: {
                checked: Math.max(spellChecked.length, spellCheckedCount),
                unchecked: segments.length - Math.max(spellChecked.length, spellCheckedCount),
                with_errors: withSpellErrors.length,
                without_errors: spellChecked.length - withSpellErrors.length,
                last_check: null
            },
            ai_correction: {
                processed: aiProcessed.length,
                unprocessed: segments.length - aiProcessed.length,
                unchanged: segments.filter(s => s.ai_correction_type === 'unchanged').length,
                ai_corrected: segments.filter(s => s.ai_correction_type === 'ai_corrected').length,
                needs_review: segments.filter(s => s.ai_correction_type === 'needs_review').length,
                last_correction: null
            },
            manual_editing: {
                edited_segments: manuallyEdited.length,
                total_edits: segments.reduce((sum, s) => sum + (s.manual_edit_count || 0), 0),
                last_edit: null
            },
            review: {
                needs_review: withSpellErrors.length + segments.filter(s => s.ai_correction_type === 'needs_review').length,
                completion_rate: {
                    spell_check: segments.length > 0 ? Math.round((spellChecked.length / segments.length) * 100) : 0,
                    ai_correction: segments.length > 0 ? Math.round((aiProcessed.length / segments.length) * 100) : 0
                }
            }
        };
    }, [activeSourceSegments, state.currentProject?.segments, spellCacheVersion]);
    // Sync player with active segment (only when user clicks)
    (0, react_1.useEffect)(() => {
        if (activeSegmentId && state.currentProject && playerRef.current && userClickedSegment) {
            const segment = state.currentProject.segments.find((s) => s.segment_id === activeSegmentId);
            if (segment) {
                playerRef.current.seekTo(segment.start_time_ms / 1000);
                // Reset flag after seeking
                setTimeout(() => setUserClickedSegment(false), 100);
            }
        }
    }, [activeSegmentId, state.currentProject, userClickedSegment]);
    // Update active segment based on player time (only when not user-initiated)
    (0, react_1.useEffect)(() => {
        if (state.currentProject && currentTime > 0 && !userClickedSegment) {
            const currentTimeMs = currentTime * 1000;
            const currentSegment = state.currentProject.segments.find((s) => s.start_time_ms <= currentTimeMs && s.end_time_ms >= currentTimeMs);
            if (currentSegment && currentSegment.segment_id !== activeSegmentId) {
                setActiveSegmentId(currentSegment.segment_id);
            }
        }
    }, [currentTime, state.currentProject, activeSegmentId, userClickedSegment]);
    // Auto-scroll to active segment
    (0, react_1.useEffect)(() => {
        if (activeSegmentId && segmentListRef.current) {
            const activeElement = segmentListRef.current.querySelector(`[data-segment-id="${activeSegmentId}"]`);
            if (activeElement) {
                activeElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }
        }
    }, [activeSegmentId]);
    const handleSegmentClick = (segmentId) => {
        setUserClickedSegment(true);
        setActiveSegmentId(segmentId);
    };
    const handleTextChange = async (segmentId, newText) => {
        try {
            await updateSegment(segmentId, newText);
            // Proveď rychlou lokální kontrolu pouze pro tento segment a aktualizuj cache
            const result = await spellCheckService_1.spellCheckService.checkSpelling(newText);
            const anyWindow = window;
            if (anyWindow.__spellCacheUpdate) {
                anyWindow.__spellCacheUpdate(newText, result);
            }
            // Neprováděj globální refresh – pouze lokální update stačí
        }
        catch (error) {
            console.error('Failed to update segment:', error);
        }
    };
    const handleApplySuggestion = async (segmentId, suggestionId) => {
        try {
            if (!state.currentProject)
                return;
            await api_1.projectsApi.applySuggestion(state.currentProject.project_id, segmentId, suggestionId);
            // Refresh project to get updated data
            await refreshCurrentProject();
            // Po refreshi by se měl přepočítat useMemo; pro jistotu resetneme aktivní zdroj (ponecháme stejný id)
            if (currentSourceId) {
                handleSourceChange(currentSourceId);
            }
        }
        catch (error) {
            console.error('Failed to apply suggestion:', error);
            alert('Nepodařilo se aplikovat návrh');
        }
    };
    const handleRejectSuggestion = async (segmentId, suggestionId) => {
        try {
            if (!state.currentProject)
                return;
            await api_1.projectsApi.rejectSuggestion(state.currentProject.project_id, segmentId, suggestionId);
            // Refresh project to get updated data
            await refreshCurrentProject();
        }
        catch (error) {
            console.error('Failed to reject suggestion:', error);
            alert('Nepodařilo se zamítnout návrh');
        }
    };
    // Navigation functions for keyboard shortcuts
    const handleNextSegment = () => {
        if (!state.currentProject?.segments || !activeSegmentId)
            return;
        const currentIndex = state.currentProject.segments.findIndex(s => s.segment_id === activeSegmentId);
        if (currentIndex < state.currentProject.segments.length - 1) {
            const nextSegment = state.currentProject.segments[currentIndex + 1];
            setActiveSegmentId(nextSegment.segment_id);
            setUserClickedSegment(true);
        }
    };
    const handlePreviousSegment = () => {
        if (!state.currentProject?.segments || !activeSegmentId)
            return;
        const currentIndex = state.currentProject.segments.findIndex(s => s.segment_id === activeSegmentId);
        if (currentIndex > 0) {
            const prevSegment = state.currentProject.segments[currentIndex - 1];
            setActiveSegmentId(prevSegment.segment_id);
            setUserClickedSegment(true);
        }
    };
    const handlePlayPause = () => {
        setIsPlaying(!isPlaying);
    };
    const handleEditCurrentSegment = () => {
        if (activeSegmentId) {
            setIsEditingSegment(true);
        }
    };
    const handleEditingChange = (isEditing) => {
        setIsEditingSegment(isEditing);
    };
    const handleAcceptCurrentSuggestions = async () => {
        if (!activeSegmentId || !state.currentProject)
            return;
        const activeSegment = state.currentProject.segments.find(s => s.segment_id === activeSegmentId);
        if (activeSegment?.suggestions) {
            for (const suggestion of activeSegment.suggestions) {
                if (!suggestion.applied) {
                    await handleApplySuggestion(activeSegmentId, suggestion.suggestion_id);
                }
            }
        }
    };
    const handleRejectCurrentSuggestions = async () => {
        if (!activeSegmentId || !state.currentProject)
            return;
        const activeSegment = state.currentProject.segments.find(s => s.segment_id === activeSegmentId);
        if (activeSegment?.suggestions) {
            for (const suggestion of activeSegment.suggestions) {
                if (!suggestion.applied) {
                    await handleRejectSuggestion(activeSegmentId, suggestion.suggestion_id);
                }
            }
        }
    };
    const formatDuration = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };
    const handleJumpToSegment = (index) => {
        if (state.currentProject && state.currentProject.segments[index]) {
            const segment = state.currentProject.segments[index];
            setActiveSegmentId(segment.segment_id);
            // Jump to segment time in video
            if (playerRef.current) {
                playerRef.current.seekTo(segment.start_time_ms / 1000, 'seconds');
            }
        }
    };
    const handleSeekForward = () => {
        if (playerRef.current) {
            const currentTime = playerRef.current.getCurrentTime();
            playerRef.current.seekTo(currentTime + 5, 'seconds'); // Seek 5 seconds forward
        }
    };
    const handleSeekBackward = () => {
        if (playerRef.current) {
            const currentTime = playerRef.current.getCurrentTime();
            playerRef.current.seekTo(Math.max(0, currentTime - 5), 'seconds'); // Seek 5 seconds backward
        }
    };
    const handleToggleFullscreen = () => {
        if (document.fullscreenElement) {
            document.exitFullscreen();
        }
        else {
            document.documentElement.requestFullscreen();
        }
    };
    const handleExport = () => {
        // Trigger export functionality
        if (state.currentProject) {
            // This would typically open an export dialog or start export
            console.log('Export triggered via keyboard shortcut');
            // You could implement export logic here or trigger an export modal
        }
    };
    const handleHelp = () => {
        // Show help dialog or keyboard shortcuts
        alert(`Klávesové zkratky:

Přehrávání:
• Mezerník - Přehrát/Pozastavit
• Ctrl+← - Přetočit zpět (5s)
• Ctrl+→ - Přetočit vpřed (5s)

Navigace:
• ↑ - Předchozí segment
• ↓ - Následující segment
• 1-9 - Přejít na segment

Editace:
• F2 - Editovat segment
• Enter - Přijmout návrhy
• Esc - Zamítnout návrhy
• Ctrl+Enter - Uložit editaci
• Ctrl+Esc - Zrušit editaci

Ostatní:
• Ctrl+S - Export
• Ctrl+F - Celá obrazovka
• Ctrl+H nebo ? - Nápověda`);
    };
    // Setup keyboard shortcuts
    (0, useKeyboardShortcuts_1.useKeyboardShortcuts)({
        onPlayPause: handlePlayPause,
        onNextSegment: handleNextSegment,
        onPreviousSegment: handlePreviousSegment,
        onEditSegment: handleEditCurrentSegment,
        onAcceptSuggestions: handleAcceptCurrentSuggestions,
        onRejectSuggestions: handleRejectCurrentSuggestions,
        onJumpToSegment: handleJumpToSegment,
        onSeekForward: handleSeekForward,
        onSeekBackward: handleSeekBackward,
        onToggleFullscreen: handleToggleFullscreen,
        onExport: handleExport,
        onHelp: handleHelp,
        isEditing: isEditingSegment,
        disabled: !state.currentProject || state.currentProject.status === 'processing',
    });
    // Funkce pro správu zdrojů titulků
    const handleSourceSelect = async (sourceId, sourceType) => {
        try {
            if (!state.currentProject)
                return;
            // Načti segmenty z vybraného zdroje
            const sourceData = await api_1.projectsApi.getSourceSegments(state.currentProject.project_id, sourceId);
            // Aktualizuj stav
            setCurrentSourceId(sourceId);
            setCurrentSourceType(sourceType);
            setShowSourceManager(false);
            // Refresh projektu pro načtení nových segmentů
            await refreshCurrentProject();
        }
        catch (error) {
            console.error('Error selecting source:', error);
        }
    };
    const handleSourceComparison = (sources) => {
        setShowComparison(true);
        setShowSourceManager(false);
    };
    const handleApplyComparisonChanges = async (sourceId, segments) => {
        try {
            if (!state.currentProject)
                return;
            // TODO: Implementovat aplikaci změn z porovnání
            console.log('Applying changes from comparison:', { sourceId, segments });
            setShowComparison(false);
            await refreshCurrentProject();
        }
        catch (error) {
            console.error('Error applying comparison changes:', error);
        }
    };
    if (!state.currentProject) {
        return ((0, jsx_runtime_1.jsx)("div", { className: "flex-1 flex items-center justify-center bg-gray-50", children: (0, jsx_runtime_1.jsxs)("div", { className: "text-center", children: [(0, jsx_runtime_1.jsx)("h2", { className: "text-2xl font-semibold text-gray-900 mb-2", children: "Vyberte projekt pro \u00FApravu" }), (0, jsx_runtime_1.jsx)("p", { className: "text-gray-600", children: "Klikn\u011Bte na projekt v lev\u00E9m panelu nebo vytvo\u0159te nov\u00FD." })] }) }));
    }
    if (state.currentProject.status === 'processing') {
        return ((0, jsx_runtime_1.jsx)("div", { className: "flex-1 flex items-center justify-center bg-gray-50", children: (0, jsx_runtime_1.jsxs)("div", { className: "text-center", children: [(0, jsx_runtime_1.jsx)(lucide_react_2.Loader2, { className: "w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" }), (0, jsx_runtime_1.jsx)("h2", { className: "text-2xl font-semibold text-gray-900 mb-2", children: "Zpracov\u00E1v\u00E1 se..." }), (0, jsx_runtime_1.jsx)("p", { className: "text-gray-600", children: "Video se zpracov\u00E1v\u00E1 pomoc\u00ED AI. Po\u010Dkejte pros\u00EDm." })] }) }));
    }
    if (state.currentProject.status === 'error') {
        return ((0, jsx_runtime_1.jsx)("div", { className: "flex-1 flex items-center justify-center bg-gray-50", children: (0, jsx_runtime_1.jsxs)("div", { className: "text-center", children: [(0, jsx_runtime_1.jsx)(lucide_react_2.AlertCircle, { className: "w-12 h-12 text-red-600 mx-auto mb-4" }), (0, jsx_runtime_1.jsx)("h2", { className: "text-2xl font-semibold text-gray-900 mb-2", children: "Chyba p\u0159i zpracov\u00E1n\u00ED" }), (0, jsx_runtime_1.jsx)("p", { className: "text-gray-600 mb-4", children: state.currentProject.error_message || 'Neočekávaná chyba' }), (0, jsx_runtime_1.jsx)("button", { onClick: () => window.location.reload(), className: "px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700", children: "Zkusit znovu" })] }) }));
    }
    return ((0, jsx_runtime_1.jsxs)("div", { className: "h-full flex flex-col", children: [(0, jsx_runtime_1.jsx)("div", { className: "bg-black flex-shrink-0", children: (0, jsx_runtime_1.jsx)(react_player_1.default, { ref: playerRef, url: state.currentProject.youtube_url, width: "100%", height: "300px", controls: true, playing: isPlaying, onPlay: () => setIsPlaying(true), onPause: () => setIsPlaying(false), onProgress: ({ playedSeconds }) => setCurrentTime(playedSeconds), config: {
                        youtube: {
                            playerVars: {
                                modestbranding: 1,
                                rel: 0,
                                iv_load_policy: 3,
                                showinfo: 0,
                                controls: 1,
                                enablejsapi: 1,
                                // Odstraněn origin parametr - způsoboval CORS problémy
                            },
                        },
                    } }) }), shouldShowEmptyView ? (
            /* Empty Project View */
            (0, jsx_runtime_1.jsx)("div", { className: "flex-1 flex items-center justify-center bg-gray-50", children: (0, jsx_runtime_1.jsxs)("div", { className: "max-w-md mx-auto text-center p-8", children: [(0, jsx_runtime_1.jsxs)("div", { className: "mb-6", children: [(0, jsx_runtime_1.jsx)("div", { className: "w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center", children: (0, jsx_runtime_1.jsx)(lucide_react_1.FileText, { className: "w-8 h-8 text-blue-600" }) }), (0, jsx_runtime_1.jsx)("h3", { className: "text-xl font-semibold text-gray-900 mb-2", children: "Za\u010Dn\u011Bte s va\u0161\u00EDm projektem" }), (0, jsx_runtime_1.jsx)("p", { className: "text-gray-600 mb-6", children: "Pro pr\u00E1ci s titulky pot\u0159ebujete nejd\u0159\u00EDve na\u010D\u00EDst zdroj titulk\u016F nebo vytvo\u0159it transkripci." })] }), (0, jsx_runtime_1.jsxs)("div", { className: "space-y-3", children: [(0, jsx_runtime_1.jsx)("button", { onClick: async () => {
                                        if (!state.currentProject)
                                            return;
                                        try {
                                            const languagesData = await api_1.projectsApi.getAvailableSubtitles(state.currentProject.project_id);
                                            if (languagesData.available_languages && languagesData.available_languages.length > 0) {
                                                // Otevřeme shareovaný modal v pravém panelu pomocí custom eventu
                                                window.dispatchEvent(new CustomEvent('open-language-modal', {
                                                    detail: {
                                                        projectId: state.currentProject.project_id,
                                                        languages: languagesData.available_languages
                                                    }
                                                }));
                                            }
                                            else {
                                                alert('⚠️ Pro toto video nejsou dostupné žádné titulky');
                                            }
                                        }
                                        catch (e) {
                                            alert('Nepodařilo se načíst seznam jazyků');
                                        }
                                    }, className: "w-full bg-white rounded-lg p-4 border border-gray-200 text-left hover:bg-blue-50 hover:border-blue-200 transition-colors", children: (0, jsx_runtime_1.jsxs)("div", { className: "flex items-start space-x-3", children: [(0, jsx_runtime_1.jsx)("div", { className: "flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center", children: (0, jsx_runtime_1.jsx)("span", { className: "text-blue-600 font-semibold text-sm", children: "1" }) }), (0, jsx_runtime_1.jsxs)("div", { children: [(0, jsx_runtime_1.jsx)("h4", { className: "font-medium text-gray-900 mb-1", children: "Na\u010D\u00EDst titulky" }), (0, jsx_runtime_1.jsx)("p", { className: "text-sm text-gray-600", children: "St\u00E1hn\u011Bte existuj\u00EDc\u00ED titulky z YouTube" })] })] }) }), (0, jsx_runtime_1.jsx)("div", { className: "text-center text-gray-500 text-sm", children: "nebo" }), (0, jsx_runtime_1.jsx)("button", { onClick: async () => {
                                        if (!state.currentProject)
                                            return;
                                        try {
                                            await api_1.projectsApi.downloadAudio(state.currentProject.project_id);
                                            await refreshCurrentProject();
                                        }
                                        catch (e) {
                                            alert('Chyba při stahování audia');
                                        }
                                    }, className: "w-full bg-white rounded-lg p-4 border border-gray-200 text-left hover:bg-green-50 hover:border-green-200 transition-colors", children: (0, jsx_runtime_1.jsxs)("div", { className: "flex items-start space-x-3", children: [(0, jsx_runtime_1.jsx)("div", { className: "flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center", children: (0, jsx_runtime_1.jsx)("span", { className: "text-green-600 font-semibold text-sm", children: "1" }) }), (0, jsx_runtime_1.jsxs)("div", { children: [(0, jsx_runtime_1.jsx)("h4", { className: "font-medium text-gray-900 mb-1", children: "St\u00E1hnout audio" }), (0, jsx_runtime_1.jsx)("p", { className: "text-sm text-gray-600", children: "St\u00E1hn\u011Bte audio z YouTube" })] })] }) }), (0, jsx_runtime_1.jsx)("button", { onClick: async () => {
                                        if (!state.currentProject)
                                            return;
                                        try {
                                            await api_1.projectsApi.transcribeWithWhisper(state.currentProject.project_id);
                                            await refreshCurrentProject();
                                        }
                                        catch (e) {
                                            alert('Chyba při Whisper transkripci');
                                        }
                                    }, className: "w-full bg-white rounded-lg p-4 border border-gray-200 text-left hover:bg-purple-50 hover:border-purple-200 transition-colors", children: (0, jsx_runtime_1.jsxs)("div", { className: "flex items-start space-x-3", children: [(0, jsx_runtime_1.jsx)("div", { className: "flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center", children: (0, jsx_runtime_1.jsx)("span", { className: "text-purple-600 font-semibold text-sm", children: "2" }) }), (0, jsx_runtime_1.jsxs)("div", { children: [(0, jsx_runtime_1.jsx)("h4", { className: "font-medium text-gray-900 mb-1", children: "Audio \u2192 Titulky" }), (0, jsx_runtime_1.jsx)("p", { className: "text-sm text-gray-600", children: "Vytvo\u0159te titulky z audia pomoc\u00ED Whisper AI" })] })] }) })] }), (0, jsx_runtime_1.jsx)("div", { className: "mt-6 text-sm text-gray-500", children: "Akce najdete v prav\u00E9m panelu v z\u00E1lo\u017Ece \"Projekt\"" })] }) })) : (
            /* Normal Project View */
            (0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("div", { className: "border-b border-gray-200 bg-white px-6 py-2 flex-shrink-0", children: (0, jsx_runtime_1.jsx)(SourceSelector_1.default, { sources: availableSources, activeSourceId: currentSourceId, onSourceChange: handleSourceChange, onCompareClick: () => setShowComparison(true), compact: true }) }), (0, jsx_runtime_1.jsx)("div", { className: "border-b border-gray-200 bg-white px-6 py-3 flex-shrink-0", children: (0, jsx_runtime_1.jsxs)("div", { className: "flex items-center justify-between", children: [(0, jsx_runtime_1.jsxs)("div", { children: [(0, jsx_runtime_1.jsxs)("h3", { className: "text-lg font-semibold text-gray-900", children: ["Segmenty (", filteredSegments.length, " z ", activeSourceSegments.length, ")"] }), (0, jsx_runtime_1.jsx)("p", { className: "text-sm text-gray-600", children: state.currentProject.video_title })] }), (0, jsx_runtime_1.jsxs)("div", { className: "flex items-center space-x-4 text-sm", children: [(0, jsx_runtime_1.jsxs)("div", { className: "flex items-center space-x-2", children: [(0, jsx_runtime_1.jsx)("span", { className: "text-orange-600 font-medium", children: activeSourceSegments.filter(s => s.status === 'NEEDS_REVIEW').length || 0 }), (0, jsx_runtime_1.jsx)("span", { className: "text-gray-600", children: "k revizi" })] }), (0, jsx_runtime_1.jsx)("button", { className: "p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded", onClick: () => setShowSourceManager(!showSourceManager), title: "Nastaven\u00ED zdroj\u016F", children: (0, jsx_runtime_1.jsx)(lucide_react_2.Settings, { className: "w-4 h-4" }) })] })] }) }), (0, jsx_runtime_1.jsx)("div", { className: "border-b border-gray-200 bg-gray-50 px-6 py-3 flex-shrink-0", children: (0, jsx_runtime_1.jsx)(SegmentFilters_1.default, { filters: filters, onFiltersChange: setFilters, segmentCounts: segmentCounts, projectId: state.currentProject?.project_id }) }), (0, jsx_runtime_1.jsx)("div", { ref: segmentListRef, className: "flex-1 min-h-0 overflow-y-auto bg-white", children: (0, jsx_runtime_1.jsx)("div", { className: "divide-y divide-gray-100", children: filteredSegments.length === 0 ? ((0, jsx_runtime_1.jsxs)("div", { className: "p-8 text-center text-gray-500", children: [(0, jsx_runtime_1.jsx)("p", { children: "\u017D\u00E1dn\u00E9 segmenty nevyhovuj\u00ED vybran\u00FDm filtr\u016Fm." }), (0, jsx_runtime_1.jsx)("button", { onClick: () => setFilters({ spellCheck: 'all', aiCorrection: 'all', suggestions: 'all', searchText: '' }), className: "mt-2 text-blue-600 hover:text-blue-800 underline text-sm", children: "Zru\u0161it v\u0161echny filtry" })] })) : (filteredSegments.map((segment) => ((0, jsx_runtime_1.jsx)("div", { "data-segment-id": segment.segment_id, children: (0, jsx_runtime_1.jsx)(SubtitleSegmentRow_1.default, { segment: segment, isActive: activeSegmentId === segment.segment_id, onSegmentClick: handleSegmentClick, onTextChange: handleTextChange, onApplySuggestion: handleApplySuggestion, onRejectSuggestion: handleRejectSuggestion, onEditingChange: handleEditingChange, searchTerm: filters.searchText }) }, segment.segment_id)))) }) })] })), showSourceManager && ((0, jsx_runtime_1.jsx)("div", { className: "absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50", children: (0, jsx_runtime_1.jsx)("div", { className: "bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden", children: (0, jsx_runtime_1.jsxs)("div", { className: "p-6", children: [(0, jsx_runtime_1.jsxs)("div", { className: "flex items-center justify-between mb-4", children: [(0, jsx_runtime_1.jsx)("h2", { className: "text-xl font-semibold", children: "Spr\u00E1va zdroj\u016F titulk\u016F" }), (0, jsx_runtime_1.jsx)("button", { className: "btn btn-ghost", onClick: () => setShowSourceManager(false), children: (0, jsx_runtime_1.jsx)(lucide_react_2.X, { className: "w-5 h-5" }) })] }), (0, jsx_runtime_1.jsx)(SubtitleSourceManager_1.default, { projectId: state.currentProject.project_id, onSourceSelect: handleSourceSelect, onSourceComparison: handleSourceComparison, selectedSourceId: currentSourceId || undefined, onSourcesChange: refreshCurrentProject })] }) }) })), showComparison && ((0, jsx_runtime_1.jsx)("div", { className: "absolute inset-0 bg-white z-50", children: (0, jsx_runtime_1.jsx)(SubtitleComparison_1.default, { projectId: state.currentProject.project_id, onClose: () => setShowComparison(false), onApplyChanges: handleApplyComparisonChanges }) }))] }));
};
exports.default = EditorPanel;
